import {
  LogOut,
  UserCircle,
  ClipboardList,
  SirenIcon,
  UserCog,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@uicomponents/ui/avatar";
import { Button } from "@uicomponents/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@uicomponents/ui/dropdown-menu";
import RegionToggle from "./RegionToggle";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/store";
import { useNavigate } from "@tanstack/react-router";
import {
  logout,
  selectCurrentOrganization,
  selectUser,
} from "@/store/authSlice";
import { useToast } from "@/hooks/use-toast";

const UserMenu = () => {
  const user = useSelector(selectUser);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = () => {
    dispatch(logout());
    navigate({ to: "/" });
    toast({
      title: "Logged out",
      description: "See you next time.",
    });
  };

  const handleProfileClick = () => {
    navigate({ to: "/user" });
  };

  if (!user) return null;

  const fullName =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || "User";

  const userInitials =
    fullName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  const userType = user.userType;

  const userSubtypeToDisplay =
    userType === "system_admin"
      ? "System Admin"
      : userType === "support_staff"
        ? "Support"
        : null;

  const shouldDisplayComparisonsMenuitem =
    import.meta.env.MODE === "local-development" ||
    import.meta.env.MODE === "development" ||
    import.meta.env.MODE === "staging";

  const shouldDisplayRegionToggle = import.meta.env.MODE !== "production";
  const shouldShowSupportMenuItem = ["system_admin", "support_staff"].includes(
    userType,
  );
  const shouldShowAdminMenuItems = ["system_admin"].includes(userType);
  const shouldShowOrganizationMenuItem =
    currentOrganization?.permissions.admin ||
    currentOrganization?.permissions.manageUserPermissions ||
    currentOrganization?.permissions.manageUsers;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-10 w-10 rounded-full p-0"
          data-testid="button-user-menu"
        >
          <Avatar className="h-10 w-10">
            <AvatarImage src="" alt="User avatar" />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {userInitials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">My Account</p>
            <p className="text-xs leading-none text-muted-foreground">
              {fullName}
            </p>
            {userSubtypeToDisplay && (
              <p className="text-xs font-bold text-red-500 leading-none text-muted-foreground">
                {userSubtypeToDisplay}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleProfileClick}
          className="cursor-pointer"
          data-testid="menu-item-profile"
        >
          <UserCircle className="mr-2 h-4 w-4" />
          <span>Profile & Settings</span>
        </DropdownMenuItem>
        {shouldShowOrganizationMenuItem && (
          <DropdownMenuItem
            onClick={() => navigate({ to: "/firm-settings" })}
            className="cursor-pointer"
            data-testid="menu-item-organization"
          >
            <UserCog className="mr-2 h-4 w-4" />
            <span>Firm Settings</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate({ to: "/assessments" })}
          className="cursor-pointer"
          data-testid="menu-item-assessments"
        >
          <ClipboardList className="mr-2 h-4 w-4" />
          <span>Assessments</span>
        </DropdownMenuItem>

        {shouldDisplayComparisonsMenuitem && (
          <DropdownMenuItem
            onClick={() =>
              navigate({
                to: "/assessments/static/comparisons",
              })
            }
            className="cursor-pointer"
            data-testid="menu-item-comparisons"
          >
            <ClipboardList className="mr-2 h-4 w-4" />
            <span>Comparisons</span>
          </DropdownMenuItem>
        )}

        {shouldDisplayRegionToggle && (
          <>
            <DropdownMenuSeparator />
            <RegionToggle />
          </>
        )}

        {shouldShowSupportMenuItem && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate({ to: "/support" })}>
              <SirenIcon className="mr-2 h-4 w-4 text-orange-500" />
              Support Dashboard
            </DropdownMenuItem>
          </>
        )}

        {shouldShowAdminMenuItems && (
          <>
            <DropdownMenuItem onClick={() => navigate({ to: "/admin" })}>
              <UserCog className="mr-2 h-4 w-4 text-red-500" />
              Admin Dashboard
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-destructive focus:text-destructive"
          data-testid="menu-item-logout"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
